import { isSameEpisodePlaybackExecution } from '@/_pure/episode';
import { synchronizeRecordedEpisodePlayback } from '@/recordedEpisodeNarration';
import type { EpisodePlaybackEffectBinding, EpisodePlaybackState } from '@/_contract/episodePlayback';
import type { createBrowserEpisodeNarrationOwner } from '@/episodeNarration';

type NarrationOwner = ReturnType<typeof createBrowserEpisodeNarrationOwner>;

export function createEpisodePlaybackEffectOwner<
    TState extends EpisodePlaybackState,
    TArgs extends { runtimeState: TState; playbackActive?: boolean; animationSuspended?: boolean },
>(binding: EpisodePlaybackEffectBinding<TArgs, TState>, narration: NarrationOwner) {
    let generation = 0;
    const synchronize = (args: TArgs) => {
        const playback = args.runtimeState.episode;
        if (!playback) {
            generation++;
            narration.synchronize();
            return;
        }
        const { sceneId, beat, recording, update, onCueRelease, cueReadyDuringPlayback } = binding.select(args);
        const executionGeneration = generation;
        const guardedUpdate: typeof update = reduce => update(state =>
            executionGeneration === generation
                && isSameEpisodePlaybackExecution(state.episode, playback)
                && state.sceneIndex === args.runtimeState.sceneIndex ? reduce(state) : state);
        synchronizeRecordedEpisodePlayback(narration, {
            ...recording, episodeHandle: playback.episodeHandle, sceneId, beatId: beat.id, cues: beat.cues,
        }, {
            key: `${playback.episodeHandle}:${args.runtimeState.sceneIndex ?? 0}:${playback.beatIndex}:${playback.revision}`,
            text: beat.spokenText ?? beat.explanation, sound: playback.soundEnabled, readingMs: beat.readingHoldMs,
            narrationPauses: beat.narrationPauses,
            ...(beat.narrationTextMap ? { displayText: beat.explanation, narrationTextMap: beat.narrationTextMap } : {}),
            onFinished: () => guardedUpdate(state => ({
                ...state, episode: { ...playback, ...state.episode, narrationCompleted: true },
            })),
            onMarker: index => guardedUpdate(state => ({
                ...state, episode: { ...playback, ...state.episode, narrationCueReached: index },
            })),
            onSentence: index => guardedUpdate(state => ({
                ...state, episode: { ...playback, ...state.episode, subtitleIndex: index },
            })),
            onError: message => guardedUpdate(state => ({
                ...state, episode: { ...playback, ...state.episode, isPlaying: false,
                    error: message, revision: playback.revision + 1 },
            })),
        }, playback, {
            playbackActive: args.playbackActive, animationSuspended: args.animationSuspended,
            cueReadyDuringPlayback,
            onCueComplete: () => {
                onCueRelease?.();
                guardedUpdate(state => (state.episode?.cueIndex ?? 0) === (playback.cueIndex ?? 0)
                    ? binding.continue(args, state, 'cue') : state);
            },
            onComplete: () => guardedUpdate(state => state.episode?.isPlaying
                ? binding.continue(args, state, 'beat')
                : { ...state, episode: { ...playback, ...state.episode, narrationRequested: false } }),
        });
    };
    return Object.assign(synchronize, {
        dispose: () => { generation++; narration.dispose(); },
        spokenProgress: () => narration.spokenProgress(),
        secondsSinceCue: (id: string) => narration.secondsSinceCue(id),
    });
}
